import { storage } from '@/libs/storage';

import type { ConsoleLogEntry, DebugLogEntry, NetworkLogEntry } from './types';

// Union of the per-kind shapes without the fields stamped inside `pushLog`.
// A plain `Omit<DebugLogEntry, …>` collapses the union to its common keys, so
// `method`/`level` would be rejected — this distributes the Omit per member.
type NewLogEntry = Omit<ConsoleLogEntry, 'id' | 'ts'> | Omit<NetworkLogEntry, 'id' | 'ts'>;

const CAPACITY = 200;
const STORAGE_KEY = 'debug-log/entries';
const PERSIST_DEBOUNCE_MS = 1000;

let buffer: DebugLogEntry[] = loadPersisted();
let seq = 0;

const listeners = new Set<() => void>();
let snapshot: DebugLogEntry[] = buffer.slice();
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function loadPersisted(): DebugLogEntry[] {
  try {
    const raw = storage.getString(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DebugLogEntry[]).slice(-CAPACITY) : [];
  } catch {
    return [];
  }
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      storage.set(STORAGE_KEY, JSON.stringify(buffer));
    } catch {
      // Never let persistence break the app.
    }
  }, PERSIST_DEBOUNCE_MS);
}

function emit() {
  snapshot = buffer.slice();
  listeners.forEach(listener => {
    try {
      listener();
    } catch {
      // ignore listener failures
    }
  });
}

/**
 * Appends an entry to the ring buffer. Callers pass everything except `id`/`ts`,
 * which are stamped here so ordering stays monotonic. Wrapped so a logging
 * failure can never bubble into the code being logged.
 */
export function pushLog(entry: NewLogEntry) {
  try {
    seq += 1;
    const full = { ...entry, id: `${Date.now()}-${seq}`, ts: Date.now() } as DebugLogEntry;
    buffer.push(full);
    if (buffer.length > CAPACITY) {
      buffer = buffer.slice(-CAPACITY);
    }
    schedulePersist();
    emit();
  } catch {
    // swallow — logging must be side-effect-free on failure
  }
}

export function getLogs(): DebugLogEntry[] {
  return snapshot;
}

export function clearLogs() {
  buffer = [];
  try {
    storage.remove(STORAGE_KEY);
  } catch {
    // ignore
  }
  emit();
}

/** Subscription API shaped for `useSyncExternalStore`. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
